import {Button, Input, Select, SelectItem} from "@heroui/react";
import _ from 'lodash';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import {useForm} from '@mantine/form';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import {useMounted, useToggle} from '@mantine/hooks';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import Voices from '../components/textToSpeech/Voices.jsx';

let currentAudioUrl = null;
const AudioPlayButton = ({children, trigger, src = undefined, ...props}) => {
	const [onLoadAudio, setOnLoadAudio] = useToggle();

	const {load, error, playing, togglePlayPause, isLoading, stop, src: audioUrl} = useGlobalAudioPlayer();
	const playAudio = async (url = null) => {
		currentAudioUrl = url
		if (!url) {
			toast.error('No audio file found');
			currentAudioUrl = null
			return;
		}
		try {
			await load(url, {
				autoplay: true,
				onend: () => {
					currentAudioUrl = null;
					stop();
				},
				onload: () => {
					setOnLoadAudio(false);
				}
			});
		} catch (e) {
			toast.error('No audio file found');
		}
	};
	return (
		<>
			<Button
				className="bg-default"
				{...props}
				key={src}
				isDisabled={_.isEmpty(src)}
				preventFocusOnPress
				isLoading={currentAudioUrl == src && onLoadAudio}
				onPress={() => (currentAudioUrl == src && playing) ? stop() : playAudio(src)}
				isIconOnly radius="full" color="">
				<i className={`hgi-stroke ${(currentAudioUrl == src && playing) ? 'hgi-pause' : 'hgi-play'} text-xl`}></i>
			</Button>
		</>
	);
}


const CreateProjectModal = ({onSubmitted, data, isClone = false, ...props}) => {
	const navigate = useNavigate();
	const mounted = useMounted();
	const queryClient = useQueryClient();
	const [speakerDropdown, setSpeakerDropdown] = useToggle();
	const {error, stop} = useGlobalAudioPlayer();
	const [totalVoices, setTotalVoices] = useState(1);
	const globalAudioPlayer = useGlobalAudioPlayer()


	useEffect(() => {
		if (error != null) toast.error(error)
	}, [error]);
	
	const form = useForm({
		initialValues: {
			project_id: data?.project_id || '',
			project_name: '',
			original_video_link: '',
			temp_video_link: '',
			expected_speaker: "auto",
			original_language: 'auto',
			translate_language: 'English',
			is_background_music: true
		},
	});
	
	form.watch('original_language', ({value}) => {
		
		if (value != 'auto' && value == form.values.translate_language) {
			form.setFieldValue('translate_language', 'auto')
		}
		
		form.setFieldValue('speaker_1', 'auto')
	})

	useEffect(() => {
		form.setFieldValue('project_id', data?.project_id || null);
		form.setFieldValue('project_name', data?.video_title || data?.project_name || 'Untitled project');
		form.setFieldValue('original_video_link', data?.video_link || data?.orginal_video_link || '');
		form.setFieldValue('original_language', data?.original_language || 'auto');
		form.setFieldValue('translate_language', data?.translate_language || 'auto');
		form.setFieldValue('temp_video_link', data?.s3_url || '');
		form.setFieldValue('video_duration', data?.video_duration || 0);
		form.setFieldValue('expected_speaker', "auto")
	}, [mounted]);
	
	const onSubmit = useMutation({
		mutationFn: () => repositories.project.create(form.values),
		onSuccess: () => {
			queryClient.invalidateQueries(['projects'])
			navigate('/app')
			toast.success('Project created successfully')
			if (typeof onSubmitted === 'function') {
				onSubmitted(true)
			}
		},
		onError: (error) => {
			console.log(error)
			toast.error(error?.response?.data?.detail || 'Something went wrong')
		}
	})
	
	const languages = useQuery({
		queryKey: ['get-project-languages'],
		queryFn: () => repositories.project.languages().then((response) => {
			setTimeout(() => form.setFieldValue('translate_language', _.get(response, '1.code', 'auto')), 300)
			return response;
		})
	})
	
	const speakers = useQuery({
		queryKey: ['get-project-speakers', form.values.translate_language],
		queryFn: () => repositories.speakers({translate_language: form.values.translate_language}).then((response) => {
			form.setFieldValue('speaker', 'auto')
			const newData = [{
				id: 'auto',
				speaker_name: 'auto',
				key: 'auto'
			}]
			return _.uniqBy(_.concat(newData, response), 'voice_id');
		})
	})
	
	const inputWrapper = 'min-h-12 bg-default-500/5 border-1 data-[focus=true]:!border-primary data-[hover=true]:border-primary shadow-none';
	
	const getSpeakerVoice = useMemo(() => {
		const speaker = _.find(speakers.data, (item) => item.speaker_name == form.values.speaker)
		return speaker?.speaker_voice || ''
	}, [form.values.speaker])
	
	
	const [isPopupOpen, setIsPopupOpen] = useState(true);

	const speaker_list = [
		{ key: "auto", value: "Auto detect" },
		{ key: 1, value: 1 },
		{ key: 2, value: 2 },
		{ key: 3, value: 3 },
		{ key: 4, value: 4 },
		{ key: 5, value: 5 },
		{ key: 6, value: 6 },
		{ key: 7, value: 7 },
		{ key: 8, value: 8 },
		{ key: 9, value: 9 },
		{ key: 10, value: 10 }
	];

	const handleSpeakerChange = (key, value, index) => {
		// Update the `speakers` array in the form dynamically
		if (value !== 'auto') {
			value = Number(value);
		}

		form.setFieldValue(`speakers`, (prevSpeakers) => {
			const updatedSpeakers = [...(prevSpeakers || [])];
			updatedSpeakers[index] = { [`speaker_${index + 1}`]: value };
			return updatedSpeakers;
		});
	};

	useEffect(() => {
		// Create a new `speakers` array
		const newSpeakers = Array.from(Array(totalVoices).keys()).map((index) => {
			const key = `speaker_${index + 1}`;
			return { [key]: 'auto' }; // Create a new object for the speaker
		});

		// Update the form's `speakers` array with the newly created list
		form.setFieldValue('speakers', newSpeakers);
	}, [totalVoices]);


	return (
		<>
			{isPopupOpen && (
				<div
					className="absolute inset-0 z-50 flex justify-center items-center bg-slate-600/50 backdrop-blur-sm rounded-3xl px-4">
					<div className="relative w-full max-w-[400px] overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl">
						{/* mini hero band — credits */}
						<div className="relative overflow-hidden bg-gradient-to-br from-[#f59e0b] via-[#f5a623] to-[#fbbf24] px-6 pt-8 pb-10">
							<div className="pointer-events-none absolute -top-10 -right-8 h-28 w-28 rounded-full bg-white/15 blur-2xl"></div>
							<div className="pointer-events-none absolute -bottom-10 -left-6 h-28 w-28 rounded-full bg-white/10 blur-2xl"></div>
							<div className="relative flex flex-col items-center text-center">
								<div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
									<i className="hgi-stroke hgi-coins-01 text-3xl text-white"></i>
								</div>
								<h3 className="mt-4 text-xl font-semibold text-white">Please confirm</h3>
								<p className="mt-1 text-sm font-light text-white/85">Creating this project uses credits.</p>
							</div>
						</div>

						{/* credit callout */}
						<div className="px-5 pt-5">
							<div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center">
								<p className="text-sm font-light leading-relaxed text-amber-700">
									This project requires
									<strong className="font-semibold"> {data?.token || 0} credits </strong>
									(2000 credits per minute of video).
								</p>
								<p className="mt-1 text-xs font-light text-amber-600/80">Would you like to continue?</p>
							</div>
						</div>

						{/* actions */}
						<div className="flex justify-end gap-3 px-5 py-5">
							<Button
								onPress={() => {
									if (typeof onSubmitted === 'function') {
										onSubmitted(true)
									}
									toast.error('Action cancelled');
								}}
								variant="bordered"
								className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
								Cancel
							</Button>
							<Button
								onPress={() => setIsPopupOpen(false)}
								className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95">
								Confirm
							</Button>
						</div>
					</div>
				</div>
			)
			}
			<div className="grid grid-cols-1 gap-5">
				<div>
					<label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Project name</label>
					<Input
						placeholder="Enter project name"
						type="text"
						startContent={<i className="hgi-stroke hgi-folder-01 text-sm text-slate-400"></i>}
						{...form.getInputProps('project_name')}
						classNames={{
							inputWrapper: 'h-[44px] bg-[#fcfcfd] rounded-xl border border-[#12131714] focus-within:!border-[#7659fa]',
							input: '!text-sm text-slate-700 placeholder:text-gray-400',
						}}
					/>
				</div>

				<div>
					<label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Original language</label>
					<Select
						aria-label="Original language"
						disallowEmptySelection
						scrollShadowProps={{
							isEnabled: false,
						}}
						selectedKeys={[form.values.original_language]}
						onSelectionChange={({currentKey}) => form.setFieldValue('original_language', currentKey)}
						isLoading={languages?.isLoading}
						classNames={{
							trigger: 'h-[44px] min-h-[44px] bg-[#fcfcfd] rounded-xl border border-[#12131714] data-[focus=true]:!border-[#7659fa] data-[open=true]:!border-[#7659fa] shadow-none',
							value: '!text-sm text-slate-700',
						}}>
						{languages?.data?.map((item) => (
							<SelectItem
								classNames={{
									base: 'text-dark data-[selected=true]:!bg-primary data-[selected=true]:!text-white py-3',
								}}
								textValue={item?.language}
								title={item?.language}
								key={item?.code}/>
						))}
					</Select>
				</div>

				<div>
					<label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Translate language</label>
					<Select
						aria-label="Translate language"
						scrollShadowProps={{
							isEnabled: false,
						}}
						isLoading={languages?.isLoading}
						disabledKeys={form.values.original_language != 'auto' && [form.values.original_language]}
						selectedKeys={[form.values.translate_language]}
						onSelectionChange={({currentKey}) => form.setFieldValue('translate_language', currentKey)}
						disallowEmptySelection
						classNames={{
							trigger: 'h-[44px] min-h-[44px] bg-[#fcfcfd] rounded-xl border border-[#12131714] data-[focus=true]:!border-[#7659fa] data-[open=true]:!border-[#7659fa] shadow-none',
							value: '!text-sm text-slate-700',
						}}>

						{languages?.data?.map((item) => (
							<SelectItem
								classNames={{
									base: 'text-dark data-[selected=true]:!bg-primary data-[selected=true]:!text-white py-3',
								}}
								textValue={item?.language}
								title={item?.language}
								key={item?.code}
							/>
						))}
					</Select>
				</div>

				<div className="flex items-start gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4">
					<i className="fa-solid fa-circle-exclamation text-[#9fa7b2] mt-[2px]"></i>
					<p className="text-[12px] leading-5 text-slate-400">Please specify the original language and translated language for better accuracy.</p>
				</div>

				{/*<div>*/}
				{/*	<Select*/}
				{/*		isRequired*/}
				{/*		label="Number of speakers"*/}
				{/*		labelPlacement="outside"*/}
				{/*		scrollShadowProps={{*/}
				{/*			isEnabled: false,*/}
				{/*		}}*/}
				{/*		isLoading={languages?.isLoading}*/}
				{/*		disabledKeys={form.values.original_language != 'auto' && [form.values.original_language]}*/}
				{/*		selectedKeys={[String(form.values.expected_speaker)]}*/}
				{/*		onSelectionChange={({currentKey}) => {*/}
				{/*			form.setFieldValue('expected_speaker', currentKey)*/}
				{/*			if (currentKey == 'auto') {*/}
				{/*				setTotalVoices(1)*/}
				{/*			}*/}
				{/*			else{*/}
				{/*				setTotalVoices(Number(currentKey))*/}
				{/*			}*/}

				{/*		}*/}
				{/*		}*/}
				{/*		disallowEmptySelection*/}
				{/*		classNames={{*/}
				{/*			trigger: 'min-h-14 border border-slate-500/80 bg-transparent',*/}
				{/*			label: 'text-slate-500 mb-10 top-5',*/}

				{/*		}}>*/}

				{/*		{speaker_list?.map((item) => (*/}
				{/*			<SelectItem*/}
				{/*				classNames={{*/}
				{/*					base: 'text-dark data-[selected=true]:!bg-primary data-[selected=true]:!text-white py-3',*/}
				{/*				}}*/}
				{/*				textValue={item?.value}*/}
				{/*				title={item?.value}*/}
				{/*				key={item?.key}*/}
				{/*			/>*/}
				{/*		))}*/}
				{/*	</Select>*/}
				{/*	<div className="rounded-xl  bg-[#c8ceda26] p-4 flex flex-row gap-2 mt-2">*/}
				{/*		<i className="fa-solid fa-circle-exclamation text-[#9fa7b2] mt-[2px]"></i>*/}
				{/*		<div className="flex flex-col justify-between gap-2.5 p-0 m-0">*/}
				{/*			<p className="text-[12px] leading-5 text-slate-400 m-0 p-0">If your video has multiple speakers, please select the expected speaker for better accuracy. After specifying the expected speaker, you can select speaker voices.</p>*/}
				{/*		</div>*/}
				{/*	</div>*/}
				{/*</div>*/}

				{Array.from(Array(totalVoices).keys()).map((item, index) => (
					<div key={index}>
						<label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
							{form.values.expected_speaker === 'auto' ? "Speaker" : `Speaker ${index + 1}`}
						</label>
						<Voices
							index={index}
							classNames={{
								trigger: 'border-slate-500',
							}}
							data={speakers?.data || []}
							onChange={(key, value) => handleSpeakerChange(key, value, index)}
							value={form.values?.['speakers']?.[index]?.['speaker_' + (index + 1)]}
							textValueKey="speaker_name"
							voiceKey="speaker_voice"
							valueKey="id"
						/>
					</div>
				))}

			</div>
			<div className="flex justify-end mt-6">
				<Button isLoading={onSubmit.isPending} onPress={() => {
					globalAudioPlayer.stop();
					onSubmit.mutate(form.values);
				}
				}
				        className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95">
					<i className="hgi-stroke hgi-folder-check text-[15px]"></i> Submit
				</Button>
			</div>
		</>
	)
};

export default CreateProjectModal;
